import { Bot, Globe, Server, Sparkles } from 'lucide-react';

interface AIOGEOScoresProps {
  aioScore: number;
  aioTip: string;
  geoScore: number;
  geoTip: string;
  seoScore: number;
  seoTip: string;
  aeoScore: number;
  aeoTip: string;
}

function getScoreLabel(score: number): string {
  if (score >= 80) return 'Excellent';
  if (score >= 60) return 'Good';
  if (score >= 30) return 'Needs Work';
  return 'Critical';
}

function getScoreLabelColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-blue-600';
  if (score >= 30) return 'text-yellow-600';
  return 'text-red-600';
}

function getScoreBarColor(score: number): string {
  if (score >= 80) return 'bg-green-500';
  if (score >= 60) return 'bg-blue-500';
  if (score >= 30) return 'bg-yellow-500';
  return 'bg-red-500';
}

interface ScorePanelProps {
  title: string;
  subtitle: string;
  description: string;
  score: number;
  tip: string;
  icon: React.ReactNode;
}

function ScorePanel({ title, subtitle, description, score, tip, icon }: ScorePanelProps) {
  const label = getScoreLabel(score);
  const labelColor = getScoreLabelColor(score);
  const barColor = getScoreBarColor(score);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:border-gray-300 transition-colors">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-50 border border-gray-200 text-gray-600">
            {icon}
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{title}</h3>
            <p className="text-xs text-gray-400">{subtitle}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-black">{score}</div>
          <div className="text-xs text-gray-400">/ 100</div>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className={`text-sm font-medium ${labelColor}`}>{label}</span>
          <span className="text-xs text-gray-400">{score}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${barColor}`}
            style={{ width: `${score}%` }}
          />
        </div>
      </div>

      <p className="text-xs text-gray-500 mb-3">{description}</p>

      {tip && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <p className="text-xs font-medium text-gray-700 mb-1">Top improvement</p>
          <p className="text-xs text-gray-600">{tip}</p>
        </div>
      )}
    </div>
  );
}

export function AIOGEOScores({ aioScore, aioTip, geoScore, geoTip, seoScore, seoTip, aeoScore, aeoTip }: AIOGEOScoresProps) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">AI Discoverability Scores</h2>
        <p className="text-sm text-gray-600">
          How well your site performs for AI tools like ChatGPT, Claude, Gemini, and Perplexity
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ScorePanel
          title="AIO Score"
          subtitle="AI Interaction Optimization"
          description="How easily AI systems can extract, parse, and correctly interpret your content — measuring clarity, structure, and entity definitions."
          score={aioScore}
          tip={aioTip}
          icon={<Bot className="w-5 h-5" />}
        />
        <ScorePanel
          title="GEO Score"
          subtitle="Generative Engine Optimization"
          description="How likely your content is to be cited or quoted in AI-generated answers — measuring authority signals, factual density, and E-E-A-T."
          score={geoScore}
          tip={geoTip}
          icon={<Globe className="w-5 h-5" />}
        />
        <ScorePanel
          title="SEO Score"
          subtitle="Search Engine Optimization"
          description="How well your site meets technical search engine requirements — measuring meta tags, canonical URLs, crawlability, and AI crawler access."
          score={seoScore}
          tip={seoTip}
          icon={<Server className="w-5 h-5" />}
        />
        <ScorePanel
          title="AEO Score"
          subtitle="Answer Engine Optimization"
          description="How well structured your site is to appear as a direct answer source in AI engines — measuring FAQ structure, answer-ready content, and QAPage schema."
          score={aeoScore}
          tip={aeoTip}
          icon={<Sparkles className="w-5 h-5" />}
        />
      </div>
    </div>
  );
}
